import { positionMilli, tileIndexAt } from "./board.js";
import { tileStats, unitStats } from "./data.js";
import { distSq } from "./fixed.js";
import { isOffline, type TowerState } from "./towers.js";
import type { Combatant } from "./damage.js";
import type { BattleEvent, Coord, Tick } from "./types.js";

/**
 * Steps 2, 4 and 5 of the tick: what the units do, as opposed to what is done
 * to them (02 GDD §6.2, §7 and the Units sheet's Special column).
 *
 *   2. units stopped at a barricade hit it; siege units hit towers too
 *   4. Priests heal
 *   5. Saboteurs disable a tower
 *
 * Until this existed a barricade stopped an attack permanently, because nothing
 * in the game could damage one.
 */

/** A unit that can act. UnitState satisfies this. */
export interface Actor extends Combatant {
  nextAbilityTick: Tick;
}

/** A barricade on the road. BarricadeState satisfies this. */
export interface Breakable {
  readonly tileIndex: number;
  hp: number;
}

export interface AbilityContext {
  readonly tick: Tick;
  readonly path: readonly Coord[];
  readonly units: readonly Actor[];
  readonly barricades: readonly Breakable[];
  readonly towers: readonly TowerState[];
  readonly events: BattleEvent[];
}

function onBoard(unit: Actor): boolean {
  return unit.status === "walking" || unit.status === "blocked";
}

function at(context: AbilityContext, unit: Actor): Coord {
  return positionMilli(context.path, unit.progress);
}

function withinSq(a: Coord, b: Coord, reach: number): boolean {
  return distSq(a.x, a.y, b.x, b.y) <= reach * reach;
}

/** The centre of the tile a unit currently occupies, in milli-tiles. */
function tileCentre(context: AbilityContext, progress: number): Coord {
  const tile = context.path[tileIndexAt(progress, context.path.length)];
  return tile ? { x: tile.x * 1000, y: tile.y * 1000 } : { x: 0, y: 0 };
}

// ---------------------------------------------------------------------------
// 2. attacks on barricades and towers
// ---------------------------------------------------------------------------

/**
 * Chip damage is deliberately NOT recorded as events. A queue of fifty units
 * hitting a barricade for a hundred ticks would be five thousand entries in
 * every replay, and the client re-runs the simulation anyway — so only the
 * moment that changes the battle is worth storing.
 */
export function resolveUnitAttacks(context: AbilityContext): void {
  const barricadeDamage = tileStats("barricade").unitDamagePerTick ?? 0;

  // Units stopped at a barricade hack at it. Two exclusions:
  //
  //  - the Ram, because its siege damage below is higher and would otherwise
  //    be counted twice;
  //  - the Scout, which the Units sheet calls "Intel (no combat)". Breaking a
  //    barricade is combat. Without this a lone Scout eventually chews through
  //    a Wall-10 barricade, which is plainly not what a scouting unit is for.
  //    (Same reading as D-087, which also keeps towers from shooting Scouts.)
  for (const unit of context.units) {
    if (unit.status !== "blocked") continue;
    const stats = unitStats(unit.kind);
    if (stats.siege) continue;
    if (stats.combat === false) continue;

    const barricade = liveBarricadeAt(context, Math.trunc(unit.progress / 1000));
    if (barricade) hurtBarricade(context, barricade, barricadeDamage);
  }

  // Siege units hit everything in reach, blocked or not: that is what the Ram
  // is for, and why leading with one is the answer to a barricade.
  //
  // Reach is measured TILE TO TILE, not at sub-tile precision. A reach of "1
  // tile" means the tile next door, and a Ram walking along a tile beside a
  // tower should be hammering it the whole way. Measured from its interpolated
  // position instead, a distance of exactly 1.0 is satisfied at a single
  // instant, and the Ram would land one hit in passing — which is not a siege
  // unit. Towers keep using sub-tile precision, because a shot genuinely does
  // care where in a tile its target is.
  for (const unit of context.units) {
    if (!onBoard(unit)) continue;
    const siege = unitStats(unit.kind).siege;
    if (!siege) continue;

    const from = tileCentre(context, unit.progress);

    for (const barricade of context.barricades) {
      if (barricade.hp <= 0) continue;
      const target = tileCentre(context, barricade.tileIndex * 1000);
      if (!withinSq(from, target, siege.reachMilli)) continue;
      hurtBarricade(context, barricade, siege.damagePerTick);
    }

    for (const tower of context.towers) {
      if (tower.hp <= 0) continue;
      if (!withinSq(from, tower.posMilli, siege.reachMilli)) continue;
      hurtTower(context, tower, siege.damagePerTick);
    }
  }
}

function liveBarricadeAt(context: AbilityContext, tileIndex: number): Breakable | undefined {
  return context.barricades.find((b) => b.tileIndex === tileIndex && b.hp > 0);
}

function hurtBarricade(context: AbilityContext, barricade: Breakable, damage: number): void {
  if (barricade.hp <= 0) return;
  barricade.hp -= damage;
  if (barricade.hp > 0) return;

  barricade.hp = 0;
  // The road is open again; movement stops treating this tile as a wall.
  context.events.push({ tick: context.tick, type: "barricade_down", a: barricade.tileIndex });
}

/**
 * A tower is never destroyed outright — below the offline threshold it simply
 * stops firing and repairs itself between battles (Assumptions sheet). So the
 * event worth recording is the moment it falls silent.
 */
function hurtTower(context: AbilityContext, tower: TowerState, damage: number): void {
  const wasOffline = isOffline(tower);
  tower.hp = Math.max(0, tower.hp - damage);
  tower.involved = true;
  if (!wasOffline && isOffline(tower)) {
    context.events.push({ tick: context.tick, type: "tower_offline", a: tower.id });
  }
}

// ---------------------------------------------------------------------------
// 4. healing
// ---------------------------------------------------------------------------

/**
 * Priests heal nearby allies in pulses (D-085): 3 HP every 5 ticks averages to
 * the sheet's 6 HP/s without ever needing a fraction.
 *
 * A Priest does not heal itself — the Units sheet says "allies".
 */
export function applyHealing(context: AbilityContext): void {
  for (const priest of context.units) {
    if (!onBoard(priest)) continue;
    const heal = unitStats(priest.kind).heal;
    if (!heal) continue;
    if (context.tick < priest.nextAbilityTick) continue;

    priest.nextAbilityTick = context.tick + heal.intervalTicks;
    const from = at(context, priest);

    for (const ally of context.units) {
      if (ally.id === priest.id) continue;
      if (!onBoard(ally)) continue;

      const full = unitStats(ally.kind).hp;
      if (ally.hp >= full) continue;
      if (!withinSq(from, at(context, ally), heal.reachMilli)) continue;

      const before = ally.hp;
      ally.hp = Math.min(full, ally.hp + heal.amount);
      context.events.push({
        tick: context.tick,
        type: "heal",
        a: ally.id,
        b: priest.id,
        c: ally.hp - before,
      });
    }
  }
}

// ---------------------------------------------------------------------------
// 5. sabotage
// ---------------------------------------------------------------------------

/**
 * A Saboteur silences the nearest tower in reach for a few seconds, then has to
 * wait out its cooldown.
 *
 * "Nearest tower" is taken literally from the Units sheet, so a Saboteur can
 * re-disable a tower that is already silent rather than picking a different
 * one — which extends that tower's downtime instead of spreading it. Same
 * literal reading as the Frost Tower's targeting; worth revisiting together
 * once there are playtests.
 */
export function applySabotage(context: AbilityContext): void {
  for (const saboteur of context.units) {
    if (!onBoard(saboteur)) continue;
    const disable = unitStats(saboteur.kind).disable;
    if (!disable) continue;
    if (context.tick < saboteur.nextAbilityTick) continue;

    const from = at(context, saboteur);
    let nearest: TowerState | null = null;
    let nearestDistance = Number.MAX_SAFE_INTEGER;

    for (const tower of context.towers) {
      const d2 = distSq(from.x, from.y, tower.posMilli.x, tower.posMilli.y);
      if (d2 > disable.reachMilli * disable.reachMilli) continue;
      // Ties go to the lowest tower id, so the choice is never ambiguous.
      if (d2 < nearestDistance) {
        nearest = tower;
        nearestDistance = d2;
      }
    }

    if (!nearest) continue;

    nearest.disabledUntilTick = context.tick + disable.durationTicks;
    nearest.involved = true;
    saboteur.nextAbilityTick = context.tick + disable.cooldownTicks;
    context.events.push({
      tick: context.tick,
      type: "sabotage",
      a: saboteur.id,
      b: nearest.id,
    });
  }
}
